import { getUserData, setUserDataShop } from '../../helpers/user';
import { ApiShop, ShopStateStatusEnum } from '../../types/shop';
import { storeLocatorStore, storeLocatorStoreFromBO } from './storeLocator';

let selection: Promise<ApiShop | null> = Promise.resolve(null);
let pending = false;

export const isShopUrlSelectionPending = (): boolean => pending;
export const waitForShopUrlSelection = (): Promise<ApiShop | null> => selection;

/**
 * Preserve the point of sale selected on the shop subdomain managed by LocalRanker
 * when the visitor follows a link to TRYBA. LocalRanker passes the shop's client
 * number in the `magasin` URL parameter (e.g. ?magasin=G263C).
 * Since localStorage is not shared between these origins, TRYBA resolves that
 * number through its APIs and saves the selected point of sale in user_data.shop.
 * Resolve once per page, including pages without the standard header.
 */
export const initShopUrlSelection = (): void => {
    const codes = new URLSearchParams(window.location.search).getAll('magasin');
    if (codes.length !== 1 || !/^[a-z0-9]+$/i.test(codes[0])) {
        return;
    }

    const code = codes[0];
    pending = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    // Defer until the synchronous bootstrap has normalized legacy user_data.
    selection = Promise.resolve().then(async (): Promise<ApiShop | null> => {
        const previousCode = getUserData()?.shop?.clientNumber ?? null;
        const stores = await storeLocatorStore(code, controller.signal);
        if (!Array.isArray(stores) || !stores.some((store) => store?.code === code)) {
            throw new Error('Store Locator returned a different shop');
        }

        const shop: ApiShop = await storeLocatorStoreFromBO(code, controller.signal);
        if (shop.clientNumber !== code || shop.state !== ShopStateStatusEnum.OPENED
            || !['name', 'city', 'zip', 'address'].every((key) => typeof shop[key] === 'string' && shop[key].length > 0)) {
            throw new Error('Shop is unavailable or its response is invalid');
        }

        // The BO response may have no URL; the store locator already supports this route.
        shop.url = typeof shop.url === 'string' && shop.url.length > 0
            ? shop.url : `/espace-conseil/?shop=${encodeURIComponent(code)}`;

        // A selection made while the APIs were loading takes priority.
        if ((getUserData()?.shop?.clientNumber ?? null) !== previousCode) {
            return null;
        }

        setUserDataShop(shop.phone, shop.zip, shop.city, shop.address, shop.name,
            shop.clientNumber, shop.url, shop.latitude, shop.longitude);

        const url = new URL(window.location.href);
        url.searchParams.delete('magasin');
        window.history.replaceState(window.history.state, '', url);
        window.dispatchEvent(new CustomEvent('user-shop-updated', { detail: shop }));
        return shop;
    }).catch((error) => {
        console.error('Unable to select the shop from the URL', error);
        return null;
    }).finally(() => {
        window.clearTimeout(timeout);
        pending = false;
    });
};
